import i18n from 'i18next';
import { initReactI18next } from 'react-i18next';
import LanguageDetector from 'i18next-browser-languagedetector';

const resources = {
  dk: {
    translation: {
      nav: {
        home: 'Hjem',
        services: 'Tjenesteydelser',
        methodology: 'Metoden',
        cases: 'Cases',
        about: 'Om Os',
        blog: 'Blog',
        contact: 'Kontakt',
        admin: 'Administration',
        signOut: 'Log ud',
      },
      hero: {
        title: 'Fra kompleksitet til klarhed',
        subtitle: 'AI-forstærket visuel facilitering',
        description: 'Vi transformerer komplekse strategiske udfordringer til klar handling gennem visuel tænkning og kunstig intelligens',
        cta: 'Book gratis konsultation',
        secondaryCta: 'Se hvordan det virker',
      },
      trust: {
        title: 'Betroet af førende organisationer',
        years: 'Års erfaring',
        workshops: 'Succesfulde workshops',
        satisfaction: 'Kundetilfredshed',
        clients: 'Virksomheder betjent',
      },
      concept: {
        title: 'FraimeWorks konceptet',
        subtitle: 'To timer. To deltagere. To facilitatorer. To møder. To opkald.',
        result: 'Et kraftfuldt resultat',
        hours: '2 timer',
        hoursDesc: 'Fokuseret, effektiv session',
        participants: '2 deltagere',
        participantsDesc: 'Beslutningstagere med fokus',
        facilitators: '2 facilitatorer',
        facilitatorsDesc: 'Visuel + AI ekspertise',
        meetings: '2 møder',
        meetingsDesc: 'Forberedelse + gennemførelse',
        calls: '2 opkald',
        callsDesc: 'Opfølgning + implementering',
        learnMore: 'Lær mere om metoden',
      },
      services: {
        title: 'Vores tjenesteydelser',
        subtitle: 'Skræddersyede løsninger til dine strategiske behov',
        viewAll: 'Se alle tjenester',
        bookConsultation: 'Book konsultation',
        strategy: {
          title: 'Strategisk planlægning',
          description: 'Transformér komplekse strategier til klare, handlingsrettede planer gennem visuel facilitering og AI-drevet indsigt',
          fullDescription: 'Strategisk planlægning kan være overvældende med modsatrettede prioriteter, komplekse stakeholders og uklare mål. Vi hjælper dig med at skabe klarhed gennem vores AI-forstærkede visuelle facilitering.',
          benefits: {
            title: 'Nøglefordele',
            clarity: 'Krystalklare strategiske mål og prioriteter',
            alignment: 'Fælles forståelse på tværs af organisationen',
            actionable: 'Konkrete handlingsplaner med tidslinjer',
            documented: 'AI-genereret dokumentation klar til implementering',
          },
          outcomes: {
            title: 'Typiske resultater',
            item1: 'Visuel strategisk ramme der guider beslutninger',
            item2: '90-dages handlingsplan med klare ejere',
            item3: 'Identificering af nøglemetrikker og KPI\'er',
            item4: 'Stakeholder-kommunikationsplan',
          },
        },
        innovation: {
          title: 'Innovations workshops',
          description: 'Frembring gennembrudsideer og accelerer innovation med struktureret visuel tænkning og AI-forstærket brainstorming',
          fullDescription: 'Innovation kræver både kreativitet og struktur. Vores workshops kombinerer visuelle teknikker med AI-værktøjer for at generere, evaluere og udvikle innovative løsninger hurtigt.',
          benefits: {
            title: 'Nøglefordele',
            ideas: 'Hurtig generering af 50+ kvalificerede ideer',
            evaluation: 'Struktureret evaluering og prioritering',
            prototyping: 'Visuelle prototyper og koncepter',
            momentum: 'Bevaring af innovationsmomentum efter sessionen',
          },
          outcomes: {
            title: 'Typiske resultater',
            item1: 'Top 5 innovationskoncepter med visuelle beskrivelser',
            item2: 'Evalueringsmatrix med ROI-estimater',
            item3: 'Prototype-roadmap for test og validering',
            item4: 'AI-genererede markedsanalyser',
          },
        },
        alignment: {
          title: 'Team alignment',
          description: 'Skab fælles forståelse og retning i tværfunktionelle teams gennem visuelle rammer og faciliteret dialog',
          fullDescription: 'Når teams arbejder på tværs af funktioner og geografi, opstår der ofte misforståelser og konfliktende prioriteter. Vi faciliterer alignment gennem visuelle værktøjer der gør det usynlige synligt.',
          benefits: {
            title: 'Nøglefordele',
            understanding: 'Delt mental model og fælles sprog',
            trust: 'Styrket tillid gennem åben dialog',
            efficiency: 'Reduceret mødetid og hurtigere beslutninger',
            engagement: 'Øget engagement og ejerskab',
          },
          outcomes: {
            title: 'Typiske resultater',
            item1: 'Team charter med fælles mål og værdier',
            item2: 'Visuel RACI-matrix for roller og ansvar',
            item3: 'Beslutnings-framework for fremtidige valg',
            item4: 'Kommunikationsprotokol og mødestruktur',
          },
        },
        problemSolving: {
          title: 'Kompleks problemløsning',
          description: 'Knæk vanskelige udfordringer ved at visualisere systemiske sammenhænge og generere AI-drevne løsningsforslag',
          fullDescription: 'Komplekse problemer har sjældent simple løsninger. Vi hjælper dig med at forstå problemets systemiske natur og identificere løftestænger for forandring gennem visuel analyse og AI-støttet tænkning.',
          benefits: {
            title: 'Nøglefordele',
            systemsThinking: 'Forståelse af årsag-virkning sammenhænge',
            rootCause: 'Identificering af grundlæggende årsager',
            solutions: 'Multiple løsningsveje evalueret',
            implementation: 'Realistisk implementeringsstrategi',
          },
          outcomes: {
            title: 'Typiske resultater',
            item1: 'Systemdiagram der viser sammenhænge',
            item2: 'Root cause analyse med prioriterede indsatsområder',
            item3: '3-5 løsningsscenarier med for/imod',
            item4: 'Implementeringsplan med risikomitigering',
          },
        },
      },
      why: {
        title: 'Hvorfor FraimeWorks?',
        aiEnhanced: {
          title: 'AI-forstærket facilitering',
          description: 'Vi kombinerer menneskelig intuition med AI for dybere indsigt og hurtigere resultater',
        },
        visualThinking: {
          title: 'Visuel tænkning ekspertise',
          description: 'Årtiers erfaring med at forvandle kompleksitet til klare, visuelle rammer',
        },
        provenMethod: {
          title: 'Dokumenteret metode',
          description: 'Vores 4x2 koncept leverer konsistente, målbare resultater',
        },
        actionable: {
          title: 'Handlingsrettede resultater',
          description: 'Hver session slutter med klare næste skridt og implementeringsplaner',
        },
      },
      process: {
        title: 'Sådan arbejder vi',
        subtitle: 'En struktureret tilgang der leverer resultater',
        preparation: {
          title: 'Kontekst & forberedelse',
          description: 'Vi lærer din udfordring at kende og skræddersyer tilgangen',
        },
        facilitation: {
          title: 'Visual faciliteringsession',
          description: 'Intensive, fokuserede sessioner med AI-drevet dokumentation',
        },
        documentation: {
          title: 'AI-drevet dokumentation',
          description: 'Automatisk opsamling og strukturering af indsigter',
        },
        followUp: {
          title: 'Opfølgning & implementering',
          description: 'Sikring af at indsigter bliver til konkrete handlinger',
        },
      },
      cta: {
        title: 'Klar til at transformere kompleksitet til klarhed?',
        button: 'Kontakt os i dag',
        secondaryButton: 'Download gratis guide',
      },
      footer: {
        newsletter: {
          title: 'Tilmeld dig vores nyhedsbrev',
          placeholder: 'Din email',
          button: 'Tilmeld',
        },
        quickLinks: 'Hurtige links',
        followUs: 'Følg os',
        rights: 'Alle rettigheder forbeholdes',
      },
      contact: {
        title: 'Kontakt os',
        subtitle: 'Lad os tale om hvordan vi kan hjælpe dig',
        form: {
          title: 'Send os en besked',
          name: 'Navn',
          namePlaceholder: 'Dit fulde navn',
          email: 'Email',
          emailPlaceholder: 'din@email.dk',
          phone: 'Telefon',
          phonePlaceholder: '+45 12 34 56 78',
          company: 'Firma',
          companyPlaceholder: 'Din virksomhed',
          service: 'Interesseret i',
          servicePlaceholder: 'Vælg en tjeneste',
          message: 'Besked',
          messagePlaceholder: 'Fortæl os om din udfordring...',
          consent: 'Jeg accepterer behandling af mine data i overensstemmelse med privatlivspolitikken',
          submit: 'Send besked',
          success: 'Tak for din besked!',
          successDescription: 'Vi vender tilbage til dig inden for 24 timer.',
          error: 'Der skete en fejl',
          errorDescription: 'Prøv venligst igen senere.',
        },
        info: {
          title: 'Kontaktinformation',
          email: 'Email',
          phone: 'Telefon',
          linkedin: 'LinkedIn',
        },
        methods: {
          title: 'Foretrukken kontaktmetode',
          email: 'Email',
          phone: 'Telefon',
        },
        faq: {
          title: 'Ofte stillede spørgsmål',
          q1: 'Hvor lang tid tager en typisk session?',
          a1: 'Vores 4x2 koncept inkluderer to 2-timers møder: et forberedelsesmøde og en faciliteringssession. I alt kan du forvente cirka 4-6 timers engagement.',
          q2: 'Arbejder I både online og fysisk?',
          a2: 'Ja, vi faciliterer både online via Zoom/Teams og fysisk på jeres lokation eller på vores faciliteter.',
          q3: 'Hvad koster jeres tjenester?',
          a3: 'Priserne varierer afhængigt af kompleksitet og omfang. Kontakt os for et skræddersyet tilbud baseret på dine behov.',
          q4: 'Hvor hurtigt kan I komme i gang?',
          a4: 'Vi kan typisk starte inden for 1-2 uger, afhængigt af vores kalender og dit behov.',
        },
      },
      auth: {
        title: 'Velkommen',
        description: 'Log ind eller opret en konto',
        login: 'Log ind',
        logout: 'Log ud',
        signup: 'Opret konto',
        email: 'Email',
        emailPlaceholder: 'din@email.dk',
        password: 'Adgangskode',
        passwordPlaceholder: 'Indtast adgangskode',
        confirmPassword: 'Bekræft adgangskode',
        confirmPasswordPlaceholder: 'Bekræft adgangskode',
        loginButton: 'Log ind',
        signupButton: 'Opret konto',
        loading: 'Behandler...',
        loginSuccess: 'Login succesfuldt',
        loginSuccessDesc: 'Velkommen tilbage!',
        signupSuccess: 'Konto oprettet',
        signupSuccessDesc: 'Velkommen til FraimeWorks!',
        checkEmail: 'Tjek din email for bekræftelse',
        error: 'Fejl',
        loginError: 'Login mislykkedes. Tjek dine oplysninger.',
        signupError: 'Kunne ikke oprette konto.',
        passwordMismatch: 'Adgangskoderne matcher ikke',
      },
      admin: {
        title: 'Admin dashboard',
        description: 'Administrer dit website indhold',
        tabs: {
          services: 'Tjenester',
          cases: 'Cases',
          blog: 'Blog',
          team: 'Team',
          testimonials: 'Anbefalinger',
          training: 'Charlotte AI',
          users: 'Brugere',
        },
        services: {
          title: 'Tjenester',
          addNew: 'Tilføj ny tjeneste',
          titleDk: 'Titel (DK)',
          titleEn: 'Titel (EN)',
          status: 'Status',
          actions: 'Handlinger',
          published: 'Udgivet',
          draft: 'Kladde',
        },
        cases: {
          title: 'Cases',
          addNew: 'Tilføj ny case',
          client: 'Klient',
          industry: 'Branche',
          status: 'Status',
          actions: 'Handlinger',
          published: 'Udgivet',
          draft: 'Kladde',
        },
        blog: {
          title: 'Blogindlæg',
          addNew: 'Tilføj nyt indlæg',
          titleDk: 'Titel (DK)',
          author: 'Forfatter',
          publishedDate: 'Udgivelsesdato',
          status: 'Status',
          actions: 'Handlinger',
          published: 'Udgivet',
          draft: 'Kladde',
        },
        team: {
          title: 'Teammedlemmer',
          addNew: 'Tilføj nyt medlem',
          name: 'Navn',
          roleDk: 'Rolle (DK)',
          email: 'Email',
          status: 'Status',
          actions: 'Handlinger',
          published: 'Udgivet',
          draft: 'Kladde',
        },
        testimonials: {
          title: 'Anbefalinger',
          addNew: 'Tilføj ny anbefaling',
          client: 'Klient',
          company: 'Firma',
          rating: 'Bedømmelse',
          status: 'Status',
          actions: 'Handlinger',
          published: 'Udgivet',
          draft: 'Kladde',
        },
        training: {
          title: 'Charlotte AI træning',
          subtitle: 'Tilføj viden og information som Charlotte kan bruge når hun chatter med besøgende',
          addNew: 'Tilføj træningsmateriale',
          form: {
            title: 'Tilføj træningsmateriale',
            editTitle: 'Rediger træningsmateriale',
            titleLabel: 'Titel',
            titlePlaceholder: 'Titel på træningsmaterialet',
            categoryLabel: 'Kategori (valgfri)',
            categoryPlaceholder: 'f.eks., FAQ, Priser, Proces',
            textInput: 'Tekst input',
            fileUpload: 'Fil upload',
            contentLabel: 'Indhold',
            contentPlaceholder: 'Tilføj detaljeret information som Charlotte skal kende...',
            uploadLabel: 'Upload træningsdokument',
            supportedFormats: 'Understøttede formater: PDF, Word (DOCX/DOC), Tekst, Markdown, PowerPoint (PPTX/PPT)',
            activeLabel: 'Aktiv (Charlotte vil bruge denne viden)',
            cancel: 'Annuller',
            create: 'Opret',
            update: 'Opdater',
            saving: 'Gemmer...',
            successCreate: 'Træningsmateriale oprettet',
            successUpdate: 'Træningsmateriale opdateret',
          },
          category: 'Kategori',
          status: 'Status',
          actions: 'Handlinger',
          published: 'Udgivet',
          draft: 'Kladde',
        },
        users: {
          title: 'Brugeradministration',
          email: 'Email',
          createdAt: 'Oprettet',
          role: 'Rolle',
          actions: 'Handlinger',
          adminRole: 'Admin',
          noRole: 'Ingen rolle',
          makeAdmin: 'Gør til admin',
          removeAdmin: 'Fjern admin',
          confirmAssign: 'Bekræft admin tildeling',
          confirmAssignDesc: 'Er du sikker på at du vil give {{email}} admin-rettigheder?',
          confirmRemove: 'Bekræft fjernelse af admin',
          confirmRemoveDesc: 'Er du sikker på at du vil fjerne admin-rettigheder fra {{email}}?',
          confirm: 'Bekræft',
          cancel: 'Annuller',
          roleAssigned: 'Admin-rolle tildelt',
          roleRemoved: 'Admin-rolle fjernet',
          fetchError: 'Kunne ikke hente brugere',
          assignError: 'Kunne ikke tildele rolle',
          removeError: 'Kunne ikke fjerne rolle',
        },
      },
      cookie: {
        message: 'Vi bruger cookies for at forbedre din oplevelse på vores website.',
        accept: 'Accepter alle',
        reject: 'Afvis alle',
        learnMore: 'Læs mere i vores',
        cookiePolicy: 'Cookie politik',
        privacyPolicy: 'Privatlivspolitik',
        and: 'og',
      },
      privacy: {
        title: 'Privatlivspolitik',
        lastUpdated: 'Sidst opdateret: december 2024',
        intro: 'Hos FraimeWorks værdsætter vi dit privatliv og er forpligtet til at beskytte dine personlige oplysninger. Denne privatlivspolitik forklarer, hvordan vi indsamler, bruger og beskytter dine data.',
        dataCollection: {
          title: 'Hvilke data vi indsamler',
          description: 'Vi indsamler følgende typer af information:',
          personal: 'Personlige oplysninger (navn, email, telefonnummer) når du kontakter os',
          contact: 'Firmaoplysninger når du udfylder formularer',
          usage: 'Brugsdata om hvordan du interagerer med vores website',
          technical: 'Tekniske data (IP-adresse, browsertype, enhedsoplysninger)',
        },
        dataUsage: {
          title: 'Hvordan vi bruger dine data',
          description: 'Vi bruger dine personlige oplysninger til:',
          services: 'At levere og forbedre vores tjenester',
          communication: 'At kommunikere med dig om dine henvendelser',
          improvement: 'At analysere og forbedre vores website',
          marketing: 'At sende relevante nyhedsbreve (kun med dit samtykke)',
        },
        dataSecurity: {
          title: 'Datasikkerhed',
          description: 'Vi implementerer passende tekniske og organisatoriske foranstaltninger for at beskytte dine personlige oplysninger mod uautoriseret adgang, tab eller misbrug.',
        },
        rights: {
          title: 'Dine rettigheder',
          description: 'Under GDPR har du ret til:',
          access: 'Adgang til dine personlige data',
          correction: 'Rettelse af unøjagtige data',
          deletion: 'Sletning af dine data ("retten til at blive glemt")',
          portability: 'Dataportabilitet',
          objection: 'At gøre indsigelse mod behandling af dine data',
        },
        contact: {
          title: 'Kontakt os',
          description: 'Hvis du har spørgsmål om denne privatlivspolitik eller ønsker at udøve dine rettigheder, kan du kontakte os på:',
        },
        backHome: 'Tilbage til forsiden',
      },
      cookiePolicy: {
        title: 'Cookie politik',
        lastUpdated: 'Sidst opdateret: december 2024',
        intro: 'Denne cookie politik forklarer, hvad cookies er, hvordan vi bruger dem, og hvordan du kan administrere dine cookie-præferencer.',
        whatAreCookies: {
          title: 'Hvad er cookies?',
          description: 'Cookies er små tekstfiler, der gemmes på din enhed, når du besøger vores website. De hjælper os med at forbedre din oplevelse og forstå, hvordan vores website bruges.',
        },
        essential: {
          title: 'Nødvendige cookies',
          description: 'Disse cookies er nødvendige for, at websitet fungerer korrekt:',
          auth: 'Autentificerings cookies (holder dig logget ind)',
          language: 'Sprogpræference cookies',
          consent: 'Cookie-samtykke cookies',
        },
        analytics: {
          title: 'Analyse cookies',
          description: 'Vi bruger analyse cookies til at forstå, hvordan besøgende bruger vores website:',
          visits: 'Antal besøgende og sessioner',
          popular: 'Mest besøgte sider',
          navigation: 'Hvordan brugere navigerer på websitet',
        },
        manage: {
          title: 'Administrer cookies',
          description: 'Du kan acceptere eller afvise cookies ved hjælp af vores cookie-banner, der vises første gang du besøger websitet.',
          browserSettings: 'Du kan også administrere cookies gennem dine browserindstillinger.',
        },
        moreInfo: {
          title: 'Mere information',
          description: 'Hvis du har spørgsmål om vores brug af cookies, kan du kontakte os på:',
        },
        backHome: 'Tilbage til forsiden',
      },
      caseStudies: {
        title: 'Cases',
        subtitle: 'Se hvordan vi har hjulpet virksomheder med at transformere kompleksitet til klarhed',
        searchPlaceholder: 'Søg efter cases...',
        industryFilter: 'Branche',
        allIndustries: 'Alle brancher',
        resultsCount: '{{count}} case studies',
        challenge: 'Udfordring',
        solution: 'Løsning',
        results: 'Resultater',
        result: 'Resultat',
        readMore: 'Læs mere',
        backToList: 'Tilbage til oversigt',
        relatedServices: 'Relaterede services',
        learnMore: 'Læs mere',
        noResults: 'Ingen resultater fundet',
        noResultsDescription: 'Prøv at justere dine filtre eller søgeord',
        clearFilters: 'Ryd filtre',
        ctaTitle: 'Klar til lignende resultater?',
        ctaButton: 'Kontakt os i dag',
        ctaDetailTitle: 'Få lignende resultater',
        ctaDetailSubtitle: 'Lad os hjælpe jer med at transformere jeres komplekse udfordringer til klare resultater',
      },
    },
  },
  en: {
    translation: {
      nav: {
        home: 'Home',
        services: 'Services',
        methodology: 'Methodology',
        cases: 'Case Studies',
        about: 'About Us',
        blog: 'Blog',
        contact: 'Contact',
        admin: 'Administration',
        signOut: 'Sign Out',
      },
      hero: {
        title: 'From Complexity to Clarity',
        subtitle: 'AI-Enhanced Visual Facilitation',
        description: 'We transform complex strategic challenges into clear action through visual thinking and artificial intelligence',
        cta: 'Book Free Consultation',
        secondaryCta: 'See How It Works',
      },
      trust: {
        title: 'Trusted by Leading Organizations',
        years: 'Years Experience',
        workshops: 'Successful Workshops',
        satisfaction: 'Client Satisfaction',
        clients: 'Companies Served',
      },
      concept: {
        title: 'The FraimeWorks Concept',
        subtitle: 'Two hours. Two participants. Two facilitators. Two meetings. Two calls.',
        result: 'One powerful outcome',
        hours: '2 Hours',
        hoursDesc: 'Focused, efficient session',
        participants: '2 Participants',
        participantsDesc: 'Decision-makers with focus',
        facilitators: '2 Facilitators',
        facilitatorsDesc: 'Visual + AI expertise',
        meetings: '2 Meetings',
        meetingsDesc: 'Preparation + execution',
        calls: '2 Calls',
        callsDesc: 'Follow-up + implementation',
        learnMore: 'Learn More About The Method',
      },
      services: {
        title: 'Our Services',
        subtitle: 'Tailored solutions for your strategic needs',
        viewAll: 'View All Services',
        bookConsultation: 'Book Consultation',
        strategy: {
          title: 'Strategic Planning',
          description: 'Transform complex strategies into clear, actionable plans through visual facilitation and AI-driven insights',
          fullDescription: 'Strategic planning can be overwhelming with competing priorities, complex stakeholders, and unclear goals. We help you create clarity through our AI-enhanced visual facilitation.',
          benefits: {
            title: 'Key Benefits',
            clarity: 'Crystal-clear strategic goals and priorities',
            alignment: 'Shared understanding across the organization',
            actionable: 'Concrete action plans with timelines',
            documented: 'AI-generated documentation ready for implementation',
          },
          outcomes: {
            title: 'Typical Outcomes',
            item1: 'Visual strategic framework that guides decisions',
            item2: '90-day action plan with clear owners',
            item3: 'Identification of key metrics and KPIs',
            item4: 'Stakeholder communication plan',
          },
        },
        innovation: {
          title: 'Innovation Workshops',
          description: 'Generate breakthrough ideas and accelerate innovation with structured visual thinking and AI-enhanced brainstorming',
          fullDescription: 'Innovation requires both creativity and structure. Our workshops combine visual techniques with AI tools to generate, evaluate, and develop innovative solutions rapidly.',
          benefits: {
            title: 'Key Benefits',
            ideas: 'Rapid generation of 50+ qualified ideas',
            evaluation: 'Structured evaluation and prioritization',
            prototyping: 'Visual prototypes and concepts',
            momentum: 'Maintained innovation momentum after the session',
          },
          outcomes: {
            title: 'Typical Outcomes',
            item1: 'Top 5 innovation concepts with visual descriptions',
            item2: 'Evaluation matrix with ROI estimates',
            item3: 'Prototype roadmap for testing and validation',
            item4: 'AI-generated market analyses',
          },
        },
        alignment: {
          title: 'Team Alignment',
          description: 'Create shared understanding and direction in cross-functional teams through visual frameworks and facilitated dialogue',
          fullDescription: 'When teams work across functions and geographies, misunderstandings and conflicting priorities often arise. We facilitate alignment through visual tools that make the invisible visible.',
          benefits: {
            title: 'Key Benefits',
            understanding: 'Shared mental model and common language',
            trust: 'Strengthened trust through open dialogue',
            efficiency: 'Reduced meeting time and faster decisions',
            engagement: 'Increased engagement and ownership',
          },
          outcomes: {
            title: 'Typical Outcomes',
            item1: 'Team charter with shared goals and values',
            item2: 'Visual RACI matrix for roles and responsibilities',
            item3: 'Decision-making framework for future choices',
            item4: 'Communication protocol and meeting structure',
          },
        },
        problemSolving: {
          title: 'Complex Problem-Solving',
          description: 'Crack difficult challenges by visualizing systemic connections and generating AI-powered solution proposals',
          fullDescription: 'Complex problems rarely have simple solutions. We help you understand the systemic nature of the problem and identify leverage points for change through visual analysis and AI-supported thinking.',
          benefits: {
            title: 'Key Benefits',
            systemsThinking: 'Understanding of cause-effect relationships',
            rootCause: 'Identification of root causes',
            solutions: 'Multiple solution paths evaluated',
            implementation: 'Realistic implementation strategy',
          },
          outcomes: {
            title: 'Typical Outcomes',
            item1: 'System diagram showing connections',
            item2: 'Root cause analysis with prioritized focus areas',
            item3: '3-5 solution scenarios with pros/cons',
            item4: 'Implementation plan with risk mitigation',
          },
        },
      },
      why: {
        title: 'Why FraimeWorks?',
        aiEnhanced: {
          title: 'AI-Enhanced Facilitation',
          description: 'We combine human intuition with AI for deeper insights and faster results',
        },
        visualThinking: {
          title: 'Visual Thinking Expertise',
          description: 'Decades of experience transforming complexity into clear visual frameworks',
        },
        provenMethod: {
          title: 'Proven Methodology',
          description: 'Our 4x2 Concept delivers consistent, measurable results',
        },
        actionable: {
          title: 'Actionable Outcomes',
          description: 'Every session ends with clear next steps and implementation plans',
        },
      },
      process: {
        title: 'How We Work',
        subtitle: 'A structured approach that delivers results',
        preparation: {
          title: 'Context & Preparation',
          description: 'We learn your challenge and tailor the approach',
        },
        facilitation: {
          title: 'Visual Facilitation Session',
          description: 'Intensive, focused sessions with AI-powered documentation',
        },
        documentation: {
          title: 'AI-Powered Documentation',
          description: 'Automatic capture and structuring of insights',
        },
        followUp: {
          title: 'Follow-up & Implementation',
          description: 'Ensuring insights become concrete actions',
        },
      },
      cta: {
        title: 'Ready to transform complexity into clarity?',
        button: 'Contact Us Today',
        secondaryButton: 'Download Free Guide',
      },
      footer: {
        newsletter: {
          title: 'Subscribe to our newsletter',
          placeholder: 'Your email',
          button: 'Subscribe',
        },
        quickLinks: 'Quick Links',
        followUs: 'Follow Us',
        rights: 'All rights reserved',
      },
      contact: {
        title: 'Contact Us',
        subtitle: 'Let\'s talk about how we can help you',
        form: {
          title: 'Send us a message',
          name: 'Name',
          namePlaceholder: 'Your full name',
          email: 'Email',
          emailPlaceholder: 'your@email.com',
          phone: 'Phone',
          phonePlaceholder: '+1 234 567 890',
          company: 'Company',
          companyPlaceholder: 'Your company',
          service: 'Interested in',
          servicePlaceholder: 'Select a service',
          message: 'Message',
          messagePlaceholder: 'Tell us about your challenge...',
          consent: 'I accept the processing of my data in accordance with the privacy policy',
          submit: 'Send Message',
          success: 'Thank you for your message!',
          successDescription: 'We will get back to you within 24 hours.',
          error: 'An error occurred',
          errorDescription: 'Please try again later.',
        },
        info: {
          title: 'Contact Information',
          email: 'Email',
          phone: 'Phone',
          linkedin: 'LinkedIn',
        },
        methods: {
          title: 'Preferred Contact Method',
          email: 'Email',
          phone: 'Phone',
        },
        faq: {
          title: 'Frequently Asked Questions',
          q1: 'How long does a typical session take?',
          a1: 'Our 4x2 Concept includes two 2-hour meetings: a preparation meeting and a facilitation session. In total, you can expect about 4-6 hours of engagement.',
          q2: 'Do you work both online and in-person?',
          a2: 'Yes, we facilitate both online via Zoom/Teams and in-person at your location or at our facilities.',
          q3: 'How much do your services cost?',
          a3: 'Prices vary depending on complexity and scope. Contact us for a tailored quote based on your needs.',
          q4: 'How quickly can you get started?',
          a4: 'We can typically start within 1-2 weeks, depending on our calendar and your needs.',
        },
      },
      auth: {
        title: 'Welcome',
        description: 'Log in or create an account',
        login: 'Log In',
        logout: 'Log Out',
        signup: 'Sign Up',
        email: 'Email',
        emailPlaceholder: 'your@email.com',
        password: 'Password',
        passwordPlaceholder: 'Enter password',
        confirmPassword: 'Confirm Password',
        confirmPasswordPlaceholder: 'Confirm password',
        loginButton: 'Log In',
        signupButton: 'Sign Up',
        loading: 'Processing...',
        loginSuccess: 'Login successful',
        loginSuccessDesc: 'Welcome back!',
        signupSuccess: 'Account created',
        signupSuccessDesc: 'Welcome to FraimeWorks!',
        checkEmail: 'Check your email for confirmation',
        error: 'Error',
        loginError: 'Login failed. Check your credentials.',
        signupError: 'Could not create account.',
        passwordMismatch: 'Passwords do not match',
      },
      admin: {
        title: 'Admin Dashboard',
        description: 'Manage your website content',
        tabs: {
          services: 'Services',
          cases: 'Case Studies',
          blog: 'Blog Posts',
          team: 'Team',
          testimonials: 'Testimonials',
          training: 'Charlotte AI',
          users: 'Users',
        },
        services: {
          title: 'Services',
          addNew: 'Add New Service',
          titleDk: 'Title (DK)',
          titleEn: 'Title (EN)',
          status: 'Status',
          actions: 'Actions',
          published: 'Published',
          draft: 'Draft',
        },
        cases: {
          title: 'Case Studies',
          addNew: 'Add New Case Study',
          client: 'Client',
          industry: 'Industry',
          status: 'Status',
          actions: 'Actions',
          published: 'Published',
          draft: 'Draft',
        },
        blog: {
          title: 'Blog Posts',
          addNew: 'Add New Post',
          titleDk: 'Title (DK)',
          author: 'Author',
          publishedDate: 'Published Date',
          status: 'Status',
          actions: 'Actions',
          published: 'Published',
          draft: 'Draft',
        },
        team: {
          title: 'Team Members',
          addNew: 'Add New Member',
          name: 'Name',
          roleDk: 'Role (DK)',
          email: 'Email',
          status: 'Status',
          actions: 'Actions',
          published: 'Published',
          draft: 'Draft',
        },
        testimonials: {
          title: 'Testimonials',
          addNew: 'Add New Testimonial',
          client: 'Client',
          company: 'Company',
          rating: 'Rating',
          status: 'Status',
          actions: 'Actions',
          published: 'Published',
          draft: 'Draft',
        },
        training: {
          title: 'Charlotte AI Training',
          subtitle: 'Add knowledge and information for Charlotte to use when chatting with visitors',
          addNew: 'Add Training Material',
          form: {
            title: 'Add Training Material',
            editTitle: 'Edit Training Material',
            titleLabel: 'Title',
            titlePlaceholder: 'Training material title',
            categoryLabel: 'Category (optional)',
            categoryPlaceholder: 'e.g., FAQ, Pricing, Process',
            textInput: 'Text Input',
            fileUpload: 'File Upload',
            contentLabel: 'Content',
            contentPlaceholder: 'Add detailed information that Charlotte should know...',
            uploadLabel: 'Upload Training Document',
            supportedFormats: 'Supported formats: PDF, Word (DOCX/DOC), Text, Markdown, PowerPoint (PPTX/PPT)',
            activeLabel: 'Active (Charlotte will use this knowledge)',
            cancel: 'Cancel',
            create: 'Create',
            update: 'Update',
            saving: 'Saving...',
            successCreate: 'Training material created successfully',
            successUpdate: 'Training material updated successfully',
          },
          category: 'Category',
          status: 'Status',
          actions: 'Actions',
          published: 'Published',
          draft: 'Draft',
        },
        users: {
          title: 'User Management',
          email: 'Email',
          createdAt: 'Created',
          role: 'Role',
          actions: 'Actions',
          adminRole: 'Admin',
          noRole: 'No role',
          makeAdmin: 'Make Admin',
          removeAdmin: 'Remove Admin',
          confirmAssign: 'Confirm Admin Assignment',
          confirmAssignDesc: 'Are you sure you want to give {{email}} admin privileges?',
          confirmRemove: 'Confirm Admin Removal',
          confirmRemoveDesc: 'Are you sure you want to remove admin privileges from {{email}}?',
          confirm: 'Confirm',
          cancel: 'Cancel',
          roleAssigned: 'Admin role assigned',
          roleRemoved: 'Admin role removed',
          fetchError: 'Could not fetch users',
          assignError: 'Could not assign role',
          removeError: 'Could not remove role',
        },
      },
      cookie: {
        message: 'We use cookies to improve your experience on our website.',
        accept: 'Accept All',
        reject: 'Reject All',
        learnMore: 'Learn more in our',
        cookiePolicy: 'Cookie Policy',
        privacyPolicy: 'Privacy Policy',
        and: 'and',
      },
      privacy: {
        title: 'Privacy Policy',
        lastUpdated: 'Last updated: December 2024',
        intro: 'At FraimeWorks, we value your privacy and are committed to protecting your personal information. This privacy policy explains how we collect, use, and protect your data.',
        dataCollection: {
          title: 'What Data We Collect',
          description: 'We collect the following types of information:',
          personal: 'Personal information (name, email, phone number) when you contact us',
          contact: 'Company information when you fill out forms',
          usage: 'Usage data about how you interact with our website',
          technical: 'Technical data (IP address, browser type, device information)',
        },
        dataUsage: {
          title: 'How We Use Your Data',
          description: 'We use your personal information to:',
          services: 'Provide and improve our services',
          communication: 'Communicate with you about your inquiries',
          improvement: 'Analyze and improve our website',
          marketing: 'Send relevant newsletters (only with your consent)',
        },
        dataSecurity: {
          title: 'Data Security',
          description: 'We implement appropriate technical and organizational measures to protect your personal information against unauthorized access, loss, or misuse.',
        },
        rights: {
          title: 'Your Rights',
          description: 'Under GDPR, you have the right to:',
          access: 'Access your personal data',
          correction: 'Correct inaccurate data',
          deletion: 'Delete your data ("right to be forgotten")',
          portability: 'Data portability',
          objection: 'Object to processing of your data',
        },
        contact: {
          title: 'Contact Us',
          description: 'If you have questions about this privacy policy or wish to exercise your rights, you can contact us at:',
        },
        backHome: 'Back to Home',
      },
      cookiePolicy: {
        title: 'Cookie Policy',
        lastUpdated: 'Last updated: December 2024',
        intro: 'This cookie policy explains what cookies are, how we use them, and how you can manage your cookie preferences.',
        whatAreCookies: {
          title: 'What Are Cookies?',
          description: 'Cookies are small text files stored on your device when you visit our website. They help us improve your experience and understand how our website is used.',
        },
        essential: {
          title: 'Essential Cookies',
          description: 'These cookies are necessary for the website to function properly:',
          auth: 'Authentication cookies (keep you logged in)',
          language: 'Language preference cookies',
          consent: 'Cookie consent cookies',
        },
        analytics: {
          title: 'Analytics Cookies',
          description: 'We use analytics cookies to understand how visitors use our website:',
          visits: 'Number of visitors and sessions',
          popular: 'Most visited pages',
          navigation: 'How users navigate the website',
        },
        manage: {
          title: 'Manage Cookies',
          description: 'You can accept or reject cookies using our cookie banner that appears the first time you visit the website.',
          browserSettings: 'You can also manage cookies through your browser settings.',
        },
        moreInfo: {
          title: 'More Information',
          description: 'If you have questions about our use of cookies, you can contact us at:',
        },
        backHome: 'Back to Home',
      },
      caseStudies: {
        title: 'Cases',
        subtitle: 'See how we have helped companies transform complexity into clarity',
        searchPlaceholder: 'Search case studies...',
        industryFilter: 'Industry',
        allIndustries: 'All industries',
        resultsCount: '{{count}} case studies',
        challenge: 'Challenge',
        solution: 'Solution',
        results: 'Results',
        result: 'Result',
        readMore: 'Read more',
        backToList: 'Back to list',
        relatedServices: 'Related Services',
        learnMore: 'Learn more',
        noResults: 'No results found',
        noResultsDescription: 'Try adjusting your filters or search terms',
        clearFilters: 'Clear filters',
        ctaTitle: 'Ready for similar results?',
        ctaButton: 'Contact Us Today',
        ctaDetailTitle: 'Get Similar Results',
        ctaDetailSubtitle: 'Let us help you transform your complex challenges into clear outcomes',
      },
    },
  },
};


i18n
  .use(LanguageDetector)
  .use(initReactI18next)
  .init({
    resources,
    fallbackLng: 'dk',
    lng: localStorage.getItem('language') || 'dk',
    interpolation: {
      escapeValue: false,
    },
    detection: {
      order: ['localStorage', 'navigator'],
      caches: ['localStorage'],
    },
  });

export default i18n;
